import {ApiInterceptors, ApiRequestDs, ApiResponseDs} from "./type-declarations";
import ApiResponseCodes from "../../models/static/api-response-codes";
import axios, {AxiosError, AxiosResponse} from "axios";

/**
 * The Api executor of this application.
 */
class ApiExecutor {

    /**
     * The effects that will be run after the execution.
     */
    private get effects(): Promise<Array<Function>> {
        return new Promise<Array<Function>>(async (resolve) => {
            const UIUtils = await import('../../../../../ui/utils').then(e => e.default);
            resolve([
                UIUtils.showToastApiCallEffect.bind(UIUtils),
            ]);
        });

    };

    /**
     * The instance with which all the api calls are handled.
     */
    private readonly instance = axios.create();

    /**
     * The list of interceptor ids that the current instance uses.
     */
    private readonly instanceInterceptorIds: { request: Array<number>, response: Array<number> } = {
        request: [],
        response: []
    };

    /**
     * Injects api interceptors to the axios instance used in this interface.
     * @param interceptors
     */
    public injectInterceptors(interceptors: Partial<ApiInterceptors>): void {
        for (const id of this.instanceInterceptorIds.request)
            this.instance.interceptors.request.eject(id);
        for (const id of this.instanceInterceptorIds.response)
            this.instance.interceptors.response.eject(id);
        this.instanceInterceptorIds.request.length = 0;
        this.instanceInterceptorIds.response.length = 0;
        for (const [onFulfilled, onRejected] of interceptors.request ?? [])
            this.instanceInterceptorIds.request.push(this.instance.interceptors.request.use(onFulfilled, onRejected));
        for (const [onFulfilled, onRejected] of interceptors.response ?? [])
            this.instanceInterceptorIds.response.push(this.instance.interceptors.response.use(onFulfilled, onRejected));
    }

    /**
     * Interpolates the request object with the default values.
     */
    private interpolateRequest(request: ApiRequestDs,): ApiRequestDs {
        return {
            ...request,
            showSuccessToast: request.showSuccessToast ?? false,
            showErrorToast: request.showErrorToast ?? false,
            // showSubmitTicket: request.showSubmitTicket ?? true,
            // externalResponseDS: request.externalResponseDS ?? false,
        }
    }

    /**
     * Executes the api call with the given args and then parses the response.
     * @param request
     * @param retried
     */
    async execute(request: ApiRequestDs, retried = 0): Promise<ApiResponseDs> {
        let response: ApiResponseDs;
        request = this.interpolateRequest(request);
        let AuthBlockerService: typeof import('../../../../../core/services/auth-blocker').default | undefined;
        try {
            AuthBlockerService = (await import('../../../../../core/services/auth-blocker')).default;
        } catch {
            // The auth-blocker module failed to load. Continue the request without it.
        }
        if (AuthBlockerService && request.loginRequired !== false) {
            const [waitPromise, ignore] = AuthBlockerService.waitForAuth();
            if (!ignore) {
                await waitPromise;
                request = this.interpolateRequest(request); // re-interpolate the request in case the auth info is updated after waiting. Not needed but only to be sure.
            }
        }

        const authStamp = AuthBlockerService?.captureRequestStamp(undefined);
        let sentAuthorization = authStamp?.authorization ?? null;
        try {
            const axiosResponse = await this.instance.request(request);
            sentAuthorization = AuthBlockerService?.readAuthorizationHeader(axiosResponse?.config?.headers) ?? sentAuthorization;
            response = ApiExecutor.onResponseReceived(axiosResponse);
        } catch (e: any) {
            sentAuthorization = AuthBlockerService?.readAuthorizationHeader(e?.config?.headers ?? e?.response?.config?.headers) ?? sentAuthorization;
            response = ApiExecutor.onErrorReceived(e);
        }

        if (AuthBlockerService && authStamp && (response.code === 401 || response.code === -401) && !((request as any).bypassAuthCheck || (request as any).loginRequired === false)) {
            const recovered = await AuthBlockerService.recoverFromUnauthorized({
                retried,
                requestGeneration: authStamp.generation,
                sentAuthorization,
                retry: () => this.execute(request, retried + 1),
            });
            if (recovered) {
                return recovered;
            }
        }

        await this.runExecutionEffects(request, response);
        return response;
    }

    /**
     * Runs the effects registered in this executor after the execution of the api call.
     * @param request
     * @param response
     */
    async runExecutionEffects(request: ApiRequestDs, response: ApiResponseDs): Promise<void> {
        for (const effect of await this.effects ?? []) {
            effect(request, response);
        }
    }


    /**
     * Creates a placeholder response data-structure.
     *
     * * this method is primarily used for creating the response object when an error happens in the api call.
     * @param statusCode
     */
    private static createPlaceholderResponseDs(statusCode: number): ApiResponseDs {
        return {
            code: statusCode,
            resultFlag: false,
            data: null,
            message: undefined,
            aborted: statusCode === ApiResponseCodes.aborted,
        };
    }

    /**
     * Executes the logic for when an error is thrown while the api call is being made or after the response of the
     * api call is received.
     * @param error
     */
    private static onErrorReceived(error: AxiosError & {}): ApiResponseDs {
        if (error.response) {
            // Request made and server responded
            return ApiExecutor.createPlaceholderResponseDs(
                error.response?.status ?? ApiResponseCodes.serverNotResponded
            );
        }
        if (error.request) {
            if (axios.isCancel(error)) {
                // cancelled / aborted
                return ApiExecutor.createPlaceholderResponseDs(ApiResponseCodes.aborted);
            }
            // The request was made but no response was received
            return ApiExecutor.createPlaceholderResponseDs(ApiResponseCodes.serverNotResponded);
        }

        // Something happened in setting up the request that triggered an Error
        return ApiExecutor.createPlaceholderResponseDs(ApiResponseCodes.requestFailed);
    }

    /**
     * Creates a response data-structure from the given response object and the statusCode.
     *
     * @param response
     * @param statusCode
     */
    private static createResponseDs(response: ApiResponseDs, statusCode: number): ApiResponseDs {
        const code = response.code ?? statusCode;
        return {
            ...response,
            resultFlag: response.resultFlag ?? false,
            data: response?.data,
            code: code,
            aborted: code === ApiResponseCodes.aborted,
        };
    }

    /**
     * Executes the logic for when the response of the api call is received without any axios-specific or http
     * specific errors.
     *
     * @param response
     */
    private static onResponseReceived(response: AxiosResponse): ApiResponseDs {
        const internalResponse: ApiResponseDs = response.data;
        return ApiExecutor.createResponseDs(
            internalResponse,
            internalResponse.code ?? response.status,
        );
    }

    // ################################         EFFECTS         ################################

}


export default ApiExecutor;
